import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { type LocationOption } from './components/LocationSearchSelect/LocationSearchSelect';

export interface OriginData {
  channelsSelection: SelectionMap;
  languagesSelection: SelectionMap;
  selectedLocations: LocationOption[];
  // Tracks whether the user explicitly interacted with the language selector. Since it starts
  // fully selected ("Todos") by default, we need this flag to tell an untouched default apart
  // from an explicit "select all" action when deciding whether to show the section badge count.
  languagesTouched?: boolean;
  // Same rationale as languagesTouched, but for the channels selector - it also starts fully
  // selected ("Todos") by default once channels are fetched, so we need this flag to avoid
  // counting that auto-populated default as an active filter.
  channelsTouched?: boolean;
}

export interface FilterOption {
  id: string;
  label: string;
}

export const LANGUAGE_OPTIONS: readonly { id: string; i18nKey: string }[] = [
  { id: 'pt', i18nKey: 'globalFilter.originLanguagePt' },
  { id: 'en', i18nKey: 'globalFilter.originLanguageEn' },
  { id: 'es', i18nKey: 'globalFilter.originLanguageEs' },
  { id: 'other', i18nKey: 'globalFilter.originLanguageOthers' },
];

const buildAllSelectedMap = (ids: string[]): SelectionMap =>
  Object.fromEntries(ids.map((id) => [id, 'add' as const]));

export const buildOriginDefaultData = (
  channelIds: string[] = [],
  languageIds: string[] = LANGUAGE_OPTIONS.map((language) => language.id),
): OriginData => ({
  channelsSelection: buildAllSelectedMap(channelIds),
  languagesSelection: buildAllSelectedMap(languageIds),
  selectedLocations: [],
  languagesTouched: false,
  channelsTouched: false,
});

export const toLabelsSelection = (
  idSelection: SelectionMap,
  options: FilterOption[],
): SelectionMap => {
  const idToLabel = new Map(options.map((item) => [item.id, item.label]));
  return Object.fromEntries(
    Object.entries(idSelection)
      .filter(([id]) => idToLabel.has(id))
      .map(([id, state]) => [idToLabel.get(id)!, state]),
  );
};

export const toIdsSelection = (
  labelSelection: SelectionMap,
  options: FilterOption[],
): SelectionMap => {
  const labelToId = new Map(options.map((item) => [item.label, item.id]));
  return Object.fromEntries(
    Object.entries(labelSelection)
      .filter(([label]) => labelToId.has(label))
      .map(([label, state]) => [labelToId.get(label)!, state]),
  );
};

export const toOriginPayload = (data: OriginData) => ({
  channels: { value: Object.keys(data.channelsSelection) },
  langs: { value: Object.keys(data.languagesSelection) },
  locations: { value: data.selectedLocations },
});
